import { OptionalEmailSchema } from "@prototypical/types";

export type FixStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export const FIX_STEP_LABELS: Record<FixStep, string> = {
  1: "business type", 2: "business details", 3: "contact details", 4: "features",
  5: "integrations", 6: "inspiration", 7: "files and notes",
};

function correctionStep(field: string): FixStep | undefined {
  if (field === "business.type") return 1;
  if (/^business\.(contact|hours)/.test(field)) return 3;
  if (field.startsWith("business")) return 2;
  if (field.startsWith("capabilities")) return 4;
  if (field.startsWith("integration")) return 5;
  if (/^(inspo|voice)/.test(field)) return 6;
  if (/^(attachments|ownerNote)/.test(field)) return 7;
}

/** Supports both field-level validation and older engines' flattened errors. */
export function startProblem(error: unknown): { message: string; step?: FixStep } {
  const payload = error && typeof error === "object" && "payload" in error ? error.payload : null;
  const data = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
  if (data.code === "invalid_brief" || data.error === "invalid_brief") {
    const issues: Array<{ field: string; message: string }> = [];
    if (Array.isArray(data.issues)) {
      for (const issue of data.issues) if (issue && typeof issue.field === "string" && typeof issue.message === "string") issues.push(issue);
    }
    if (!issues.length && data.details && typeof data.details === "object" && "fieldErrors" in data.details) {
      const fields = data.details.fieldErrors;
      if (fields && typeof fields === "object") for (const [field, messages] of Object.entries(fields)) {
        if (!Array.isArray(messages)) continue;
        for (const message of messages) if (typeof message === "string") issues.push({
          field: field === "business" && /email/i.test(message) ? "business.contact.email" : field,
          message: /email/i.test(message) ? "Enter a valid email address, or leave Email blank." : `${field === "attachments" ? "Files" : field === "inspo" ? "Inspiration links" : "Site details"}: ${message}`,
        });
      }
    }
    return {
      message: issues.length ? [...new Set(issues.map(issue => issue.message))].join(" ") : "Please review your business details, contact email, inspiration links, and uploaded files. Your details are still here.",
      step: issues.map(issue => correctionStep(issue.field)).find(step => step !== undefined),
    };
  }
  const readable = typeof data.error === "string" && !/^[a-z]+(?:_[a-z]+)+$/.test(data.error) ? data.error : "Could not start your site. Your details are still here; please try again.";
  const alternatives = Array.isArray(data.alternatives) ? data.alternatives.filter(value => typeof value === "string") : [];
  return { message: readable + (alternatives.length ? ` Try: ${alternatives.join(", ")}.` : "") };
}

export function validOptionalEmail(email: string): boolean {
  return OptionalEmailSchema.safeParse(email).success;
}
